import { useMemo } from "react";
import { ResponsiveContainer, Sankey, Tooltip } from "recharts";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/utils";
import { calculateLineItemTotal } from "@/lib/finance-calculations";
import type { QuoteGroupWithItems } from "@/hooks/useQuoteEntities";
import type { LineItemType } from "@/types/finance-entities";

type SankeyNodeKind = "group" | "category" | "item";

export interface QuoteSankeyNode {
  id: string;
  name: string;
  kind: SankeyNodeKind;
  color: string;
  optional?: boolean;
}

export interface QuoteSankeyLink {
  source: number;
  target: number;
  value: number;
  color: string;
}

export interface QuoteSankeyData {
  nodes: QuoteSankeyNode[];
  links: QuoteSankeyLink[];
  itemCount: number;
  categoryCount: number;
}

const CATEGORY_DETAILS: Record<LineItemType, { label: string; color: string }> =
  {
    equipment: { label: "Equipment", color: "#0ea5e9" },
    crew: { label: "Personnel", color: "#10b981" },
    generic: { label: "Misc", color: "#f59e0b" },
  };

const GROUP_COLOR = "#6366f1";

export function buildQuoteSankeyData(
  groups: QuoteGroupWithItems[],
): QuoteSankeyData {
  const nodes: QuoteSankeyNode[] = [];
  const links: QuoteSankeyLink[] = [];
  let itemCount = 0;
  let categoryCount = 0;

  for (const group of groups) {
    const valuedItems = group.items
      .filter((item) => !item.is_title)
      .map((item) => ({
        item,
        value: calculateLineItemTotal(
          item.amount,
          item.factor,
          item.unit_price,
          item.discount,
        ),
      }))
      .filter(({ value }) => Number.isFinite(value) && value > 0);

    if (valuedItems.length === 0) continue;

    const groupIndex = nodes.length;
    nodes.push({
      id: `group:${group.id}`,
      name: group.name || "Untitled group",
      kind: "group",
      color: GROUP_COLOR,
      optional: group.is_optional,
    });

    for (const type of ["equipment", "crew", "generic"] as const) {
      const categoryItems = valuedItems.filter(
        ({ item }) => item.line_item_type === type,
      );
      if (categoryItems.length === 0) continue;

      const category = CATEGORY_DETAILS[type];
      const categoryIndex = nodes.length;
      const categoryValue = categoryItems.reduce(
        (sum, { value }) => sum + value,
        0,
      );

      nodes.push({
        id: `category:${group.id}:${type}`,
        name: category.label,
        kind: "category",
        color: category.color,
      });
      links.push({
        source: groupIndex,
        target: categoryIndex,
        value: categoryValue,
        color: category.color,
      });
      categoryCount += 1;

      for (const { item, value } of categoryItems) {
        const itemIndex = nodes.length;
        nodes.push({
          id: `item:${item.id}`,
          name: item.name || "Untitled line item",
          kind: "item",
          color: category.color,
        });
        links.push({
          source: categoryIndex,
          target: itemIndex,
          value,
          color: category.color,
        });
        itemCount += 1;
      }
    }
  }

  return { nodes, links, itemCount, categoryCount };
}

interface RenderedNode extends QuoteSankeyNode {
  x: number;
  y: number;
  dx: number;
  dy: number;
  value: number;
  depth: number;
}

interface SankeyNodeRendererProps {
  x: number;
  y: number;
  width: number;
  height: number;
  payload: RenderedNode;
}

function truncateLabel(label: string, maxLength: number) {
  return label.length > maxLength ? `${label.slice(0, maxLength - 1)}…` : label;
}

function SankeyNodeRenderer({
  x,
  y,
  width,
  height,
  payload,
}: SankeyNodeRendererProps) {
  const isGroup = payload.kind === "group";
  const isCategory = payload.kind === "category";
  const labelX = isGroup ? x - 8 : isCategory ? x + width / 2 : x + width + 8;
  const labelY = isCategory ? y - 7 : y + Math.max(10, height / 2 + 4);
  const textAnchor = isGroup ? "end" : isCategory ? "middle" : "start";
  const maxLength = isCategory ? 16 : 26;
  const label = `${truncateLabel(payload.name, maxLength)}${
    payload.optional ? " (optional)" : ""
  }`;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={Math.max(2, height)}
        rx={2}
        fill={payload.color}
        fillOpacity={payload.optional ? 0.55 : 0.88}
      />
      <text
        x={labelX}
        y={labelY}
        textAnchor={textAnchor}
        className="fill-foreground text-[11px]"
      >
        <title>{`${payload.name} — ${formatCurrency(payload.value)}`}</title>
        {label}
      </text>
    </g>
  );
}

interface RenderedLinkPayload extends Omit<
  QuoteSankeyLink,
  "source" | "target"
> {
  source: RenderedNode;
  target: RenderedNode;
}

interface SankeyLinkRendererProps {
  sourceX: number;
  targetX: number;
  sourceY: number;
  targetY: number;
  sourceControlX: number;
  targetControlX: number;
  linkWidth: number;
  payload: RenderedLinkPayload;
}

function SankeyLinkRenderer({
  sourceX,
  targetX,
  sourceY,
  targetY,
  sourceControlX,
  targetControlX,
  linkWidth,
  payload,
}: SankeyLinkRendererProps) {
  return (
    <path
      d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
      fill="none"
      stroke={payload.color}
      strokeOpacity={0.28}
      strokeWidth={Math.max(1, linkWidth)}
    />
  );
}

function SankeyLegend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
      {(
        [
          ["Groups", GROUP_COLOR],
          ["Equipment", CATEGORY_DETAILS.equipment.color],
          ["Personnel", CATEGORY_DETAILS.crew.color],
          ["Misc", CATEGORY_DETAILS.generic.color],
        ] as const
      ).map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: color }}
          />
          {label}
        </span>
      ))}
    </div>
  );
}

export function QuoteSankey({ groups }: { groups: QuoteGroupWithItems[] }) {
  const data = useMemo(() => buildQuoteSankeyData(groups), [groups]);
  const height = Math.min(
    1600,
    Math.max(420, data.itemCount * 30, data.categoryCount * 42),
  );

  if (data.links.length === 0) {
    return (
      <div className="flex h-80 items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
        Add priced line items to see the quote breakdown.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded-lg border bg-card">
      <div className="min-w-[980px]" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <Sankey
            data={data}
            node={(props) => <SankeyNodeRenderer {...props} />}
            link={(props) => <SankeyLinkRenderer {...props} />}
            nodeWidth={14}
            nodePadding={18}
            linkCurvature={0.55}
            iterations={48}
            margin={{ top: 32, right: 190, bottom: 24, left: 190 }}
          >
            <Tooltip
              formatter={(value) => formatCurrency(Number(value))}
              contentStyle={{
                background: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
                color: "hsl(var(--foreground))",
              }}
            />
          </Sankey>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function QuoteSankeyDialog({
  open,
  onOpenChange,
  groups,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: QuoteGroupWithItems[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="grid max-h-[92vh] w-[96vw] max-w-[1500px] grid-rows-[auto_minmax(0,1fr)] overflow-hidden p-0">
        <DialogHeader className="border-b px-6 pb-4 pt-6 pr-12">
          <DialogTitle>Quote Sankey</DialogTitle>
          <DialogDescription>
            Line-item totals flow from groups through Equipment, Personnel, and
            Misc. Widths reflect totals after line-item discounts.
          </DialogDescription>
          <SankeyLegend />
        </DialogHeader>
        <div className="min-h-0 overflow-auto px-6 pb-6">
          <QuoteSankey groups={groups} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
